import { useMemo, useReducer } from 'react'

// Open/collapsed state and the highlighted option of a combobox's list.
// The rules every transition keeps (#302 needed two fixes for missed ones):
// - collapsing always clears the highlighted option: aria-activedescendant
//   can't point into a hidden list, and a stale hover mustn't become the
//   Enter target after reopening;
// - a move on a collapsed list only opens it; the next move highlights.
// The list is open when it has options and isn't collapsed, so `count` is
// passed in rather than stored.

export const initialComboboxState = { collapsed: false, activeIndex: -1 }

export function comboboxReducer(state, action) {
  switch (action.type) {
    case 'collapse':
      return { collapsed: true, activeIndex: -1 }
    case 'expand':
      return state.collapsed ? { ...state, collapsed: false } : state
    case 'toggle':
      return !state.collapsed && action.count > 0
        ? { collapsed: true, activeIndex: -1 }
        : { collapsed: false, activeIndex: -1 }
    case 'move': {
      if (action.count <= 0) return state
      if (state.collapsed) return { collapsed: false, activeIndex: -1 }
      const last = action.count - 1
      const current = state.activeIndex
      const activeIndex = action.delta > 0
        ? (current < last ? current + 1 : 0)
        : (current > 0 ? current - 1 : last)
      return { ...state, activeIndex }
    }
    case 'highlight':
      return state.collapsed || state.activeIndex === action.index ? state : { ...state, activeIndex: action.index }
    case 'clearActive':
      return state.activeIndex === -1 ? state : { ...state, activeIndex: -1 }
    default:
      throw new Error(`Unknown combobox action: ${action.type}`)
  }
}

export function useComboboxState(count) {
  const [state, dispatch] = useReducer(comboboxReducer, initialComboboxState)
  const actions = useMemo(() => ({
    collapse: () => dispatch({ type: 'collapse' }),
    expand: () => dispatch({ type: 'expand' }),
    toggle: () => dispatch({ type: 'toggle', count }),
    highlight: (index) => dispatch({ type: 'highlight', index }),
    clearActive: () => dispatch({ type: 'clearActive' }),
  }), [count])

  // Returns the option it highlighted, or -1 when it only opened the list,
  // so the caller can scroll to it (keyboard moves only; hover doesn't scroll).
  const move = (delta) => {
    const action = { type: 'move', delta, count }
    dispatch(action)
    return comboboxReducer(state, action).activeIndex
  }

  return {
    open: count > 0 && !state.collapsed,
    activeIndex: state.activeIndex,
    move,
    ...actions,
  }
}
